import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import ThemeToggle from '../components/ThemeToggle';
import { ArrowLeft, ArrowRight, BarChart3, Check, Loader2, LockKeyhole, ShieldCheck } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user?.role === 'owner' && user?.email_verified === false) {
        navigate(`/verify-email?email=${encodeURIComponent(user.email)}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_85%_15%,hsl(var(--primary)/0.08),transparent_28%)]" />

      <header className="absolute inset-x-0 top-0 z-20 flex h-20 items-center justify-between px-5 sm:px-8 lg:px-10">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Back to SurviPOS home">
          <img src="/survipos%20logo.png" alt="SurviPOS logo" className="h-9 w-9 rounded-lg object-cover" />
          <div>
            <span className="block font-heading text-lg font-bold leading-none text-primary lg:text-primary-foreground">SurviPOS</span>
            <span className="text-[10px] text-muted-foreground lg:text-primary-foreground/65">by Kareem Data Labs</span>
          </div>
        </Link>
        <ThemeToggle size="icon" showLabel={false} className="bg-background/80 backdrop-blur-sm" />
      </header>

      <div className="grid min-h-screen lg:grid-cols-[minmax(0,0.92fr)_minmax(32rem,1.08fr)]">
        <section className="relative hidden overflow-hidden bg-primary px-10 pb-12 pt-28 text-primary-foreground lg:flex lg:flex-col lg:justify-center xl:px-16">
          <div className="absolute -left-24 top-1/4 h-80 w-80 rounded-full border border-primary-foreground/10" />
          <div className="absolute -left-10 top-1/3 h-56 w-56 rounded-full border border-primary-foreground/10" />
          <div className="relative mx-auto w-full max-w-lg">
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.18em] text-primary-foreground/70">Your business command centre</p>
            <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight xl:text-5xl">Start the day knowing exactly where your business stands.</h1>
            <p className="mt-6 max-w-md text-lg leading-8 text-primary-foreground/75">Sales, stock, cashflow and tax reporting stay connected—so every decision starts with a clear picture.</p>

            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {['Real-time sales overview', 'Stock and low-stock alerts', 'Cashflow and debt control', 'URA-ready reporting'].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-xl border border-primary-foreground/10 bg-primary-foreground/[0.07] px-4 py-3.5 text-sm font-medium backdrop-blur-sm">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-foreground/15"><Check className="h-3.5 w-3.5" /></span>
                  {item}
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border border-primary-foreground/10 bg-primary-foreground/[0.08] p-5 shadow-2xl shadow-black/10 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-primary-foreground/65">Today&apos;s sales</p><p className="mt-1 font-heading text-2xl font-bold">UGX 2,480,000</p></div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-foreground/15"><BarChart3 className="h-5 w-5" /></div>
              </div>
              <div className="mt-5 flex h-14 items-end gap-2" aria-hidden="true">
                {[32, 52, 43, 70, 58, 92, 76, 88].map((height, index) => <span key={index} className="flex-1 rounded-t-sm bg-primary-foreground/35" style={{ height: `${height}%` }} />)}
              </div>
            </div>
          </div>
          <p className="relative mx-auto mt-auto w-full max-w-lg pt-10 text-xs text-primary-foreground/55">Secure, URA-compliant business software for Ugandan SMEs.</p>
        </section>

        <section className="flex min-h-screen items-center justify-center px-5 pb-10 pt-28 sm:px-8 lg:px-12 lg:py-24">
          <div className="w-full max-w-md">
            <Link to="/" className="mb-9 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>

            <div className="mb-8">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><LockKeyhole className="h-5 w-5" /></div>
              <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Welcome back</h2>
              <p className="mt-2 text-muted-foreground">Sign in to open your SurviPOS workspace.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="destructive" role="alert" aria-live="polite">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email or username</Label>
                <Input id="email" type="text" autoComplete="username" placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus className="h-12 bg-background" data-testid="login-email-input" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm font-medium text-primary transition-colors hover:text-primary/80">Forgot password?</Link>
                </div>
                <PasswordInput id="password" placeholder="Enter your password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required className="h-12 bg-background" data-testid="login-password-input" />
              </div>

              <Button type="submit" className="h-12 w-full gap-2 text-base font-semibold shadow-lg shadow-primary/20" disabled={loading} data-testid="login-submit-btn">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Signing in...</> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </form>

            <div className="my-7 flex items-center gap-4"><span className="h-px flex-1 bg-border" /><span className="text-xs uppercase tracking-wider text-muted-foreground">New to SurviPOS?</span><span className="h-px flex-1 bg-border" /></div>

            <Button variant="outline" asChild className="h-12 w-full text-base"><Link to="/register" data-testid="register-link">Create a business account</Link></Button>

            <div className="mt-8 flex items-center justify-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 text-primary" /><span>Your account and business data are securely protected.</span></div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default LoginPage;
